import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { ArrowDownV2, ArrowUpV2, TableFilterIcon } from 'assets/icons';
import { classNames, sortItems } from '../../utils';
import useFadeInOnScroll from '../../views/hooks/useFadeInOnScroll';
import useInfiniteScroll from '../../views/hooks/useInfiniteScroll';
import { TableSkeleton } from '../Skeleton';
import { ObjectType } from '../../types/commonTypes';

enum CellTypeEnum {
  TH = 'th',
  TD = 'tr',
}

type TableColumnType = TableCell & {
  cellClassName?: string;
  cellType?: CellTypeEnum;
  contentTemplate?: (col: TableColumnType, translation: TFunction) => JSX.Element;
};
interface TableCell {
  text: string;
  dataRef: string;
  className?: string;
  width?: string;
  type?: CellTypeEnum;
  isVisible?: boolean;
  isExportable?: boolean;
  isSortable?: boolean;
  exportFn?: (arg: string | number, x?: ObjectType) => string;
  cellContentTemplate?: (cell: TableCell, translation: TFunction, rowData?: TableRow['rowData']) => ReactNode;
}

interface TableRow {
  id?: string;
  onClick?: (row: TableRow) => void;
  className: string;
  cells: TableCell[];
  rowData?: Record<string, unknown>;
}
interface TableProp<S> {
  columns: TableColumnType[];
  onTableEvent?: (event: TableRow | TableColumnType) => void;
  rows: TableRow[];
  className?: string;
  theadClassName?: string;
  containerClassNames?: string;
  isFadeOnScrollDisable?: boolean;
  applyfadeOnScrollClassName?: boolean;
  dataSource?: S[];
  updateDataSource?: (data: S[], sortKey?: string, isAsc?: boolean) => void;
  defaultSortKey?: string;
  sortIsAsc?: boolean;
  disableSort?: boolean;
  disableSortFunc?: () => void;
  /** Bound the table in its own scroll box (header + first column stay pinned). */
  scrollContained?: boolean;
  /** Pin the first column to the left while scrolling horizontally. */
  stickyFirstColumn?: boolean;
  /** Tailwind max-height class for the scroll box (only used when scrollContained). */
  maxHeightClass?: string;
  /** Tailwind min-width class so fluid columns stop shrinking and scroll instead. */
  minWidthClass?: string;
}

interface ISortConfig<S, K> {
  sortResult: S[];
  isAsc: boolean;
  currentSortKey: K | null;
}

const ColumnSortTable = <S, K extends keyof S>({
  columns,
  rows,
  className,
  theadClassName,
  containerClassNames,
  onTableEvent,
  isFadeOnScrollDisable = false,
  applyfadeOnScrollClassName = true,
  dataSource,
  updateDataSource,
  defaultSortKey = '',
  disableSort = false,
  disableSortFunc,
  sortIsAsc,
  scrollContained = false,
  stickyFirstColumn = false,
  maxHeightClass = 'max-h-[65vh]',
  minWidthClass = 'min-w-[1040px]',
}: TableProp<S>) => {
  const [selectedRow, setSelectedRow] = useState('');
  const { t } = useTranslation();
  const { isLoading, results: resultsRows, lastItemRef } = useInfiniteScroll(rows);

  const processIntersectionObserverEntry = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!isFadeOnScrollDisable) {
        entry.target.classList.toggle('visible', entry.isIntersecting);
        entry.target.classList.toggle('collapse', !entry.isIntersecting);
      }
    },
    [isFadeOnScrollDisable],
  );
  const results = useMemo(() => {
    return resultsRows.length ? resultsRows : rows.slice(0, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsRows]);

  const [sortConfig, setSortConfig] = useState<ISortConfig<S, K>>({
    sortResult: [],
    isAsc: true,
    currentSortKey: null,
  });

  const tableSortFunc = (sortKey: K, source: S[], config: ISortConfig<S, K>) => {
    let isAsc = config.isAsc;

    if (config.currentSortKey === sortKey) {
      isAsc = !config.isAsc;
    }

    const sortedSource = sortItems([...source], isAsc, sortKey);
    updateDataSource?.(sortedSource, sortKey as string, isAsc);
    setSortConfig({
      sortResult: sortedSource,
      isAsc,
      currentSortKey: sortKey,
    });
  };

  useEffect(() => {
    if (defaultSortKey)
      tableSortFunc(defaultSortKey as K, dataSource || [], { ...sortConfig, isAsc: sortIsAsc ?? sortConfig.isAsc });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSortKey, sortIsAsc]);

  const { isItemVisible, fadeOnScrollClassName } = useFadeInOnScroll({ isLoading, processIntersectionObserverEntry });

  const firstVisibleIndex = columns.findIndex((c) => c.isVisible !== false);
  const containerClass = scrollContained
    ? classNames('overflow-auto relative rounded-xl', maxHeightClass, containerClassNames || '')
    : classNames('overflow-x-auto overflow-y-hidden relative rounded-xl', containerClassNames || '');
  const tableClass = scrollContained
    ? `table-fixed w-full ${minWidthClass} text-sm text-left text-gray-700 dark:text-gray-200 ${className || ''}`.trim()
    : `table-auto w-full text-sm text-left text-gray-700 dark:text-gray-200 ${className || ''}`.trim();
  const stickyHeaderCellClass = (index: number) =>
    stickyFirstColumn && index === firstVisibleIndex ? 'sticky left-0 z-30 bg-gray-100 dark:bg-slate-800' : '';
  const stickyBodyCellClass = (index: number, isEven: boolean) =>
    stickyFirstColumn && index === firstVisibleIndex
      ? `sticky left-0 z-10 ${isEven ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-800'}`
      : '';

  return (
    <div className={containerClass}>
      <table className={tableClass}>
        {scrollContained && (
          <colgroup>
            {columns.map((col, index) => {
              if (col.isVisible === false) return null;
              return <col key={`colw-${index}`} style={col.width ? { width: col.width } : undefined} />;
            })}
          </colgroup>
        )}
        <thead
          className={`text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-slate-800 sticky top-0 z-20 ${
            theadClassName || ''
          }`.trim()}
        >
          <tr className="border-b-2 border-gray-200/80 dark:border-slate-600/80">
            {columns.map((col, index) => {
              const isVisible = col.isVisible !== undefined ? col.isVisible : true;
              const isSorted = sortConfig.currentSortKey === col.dataRef;
              if (!isVisible) return null;
              return (
                <th
                  key={`col-${index}`}
                  scope="col"
                  className={`${col.contentTemplate ? '' : 'py-4 px-4 whitespace-nowrap overflow-hidden'} ${
                    isSorted ? 'bg-orange/5 dark:bg-teal-500/10' : ''
                  } ${stickyHeaderCellClass(index)} ${col.className || ''} transition-colors`.trim()}
                  onClick={() =>
                    disableSort ? disableSortFunc?.() : tableSortFunc(col.dataRef as K, dataSource || [], sortConfig)
                  }
                >
                  <div className="hover:cursor-pointer flex flex-row items-center gap-1.5 min-w-0 group">
                    <span
                      title={typeof col.text === 'string' ? col.text : undefined}
                      className={`truncate min-w-0 ${
                        isSorted
                          ? 'text-orange dark:text-teal-400'
                          : 'group-hover:text-gray-700 dark:group-hover:text-gray-200'
                      }`}
                    >
                      {col.contentTemplate ? col.contentTemplate(col, t) : col.text}
                    </span>
                    <span className="flex-shrink-0">
                      {!isSorted && (
                        <TableFilterIcon className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors" />
                      )}
                      {isSorted && sortConfig.isAsc && (
                        <ArrowUpV2 className="w-3.5 h-3.5 text-orange dark:text-teal-400" />
                      )}
                      {isSorted && !sortConfig.isAsc && (
                        <ArrowDownV2 className="w-3.5 h-3.5 text-orange dark:text-teal-400" />
                      )}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100/80 dark:divide-slate-700/50">
          {results.map((row, rIndex) => {
            const rowId = row.id || `${rIndex}`;
            const isSelected = rowId === selectedRow;
            const isEven = rIndex % 2 === 0;
            let lastElemRefOption = { ref: isItemVisible };
            if (results.length === rIndex + 1) {
              lastElemRefOption = { ref: lastItemRef };
            }
            return (
              <tr
                {...lastElemRefOption}
                key={`row-${rIndex}`}
                onClick={() => {
                  if (row.onClick || onTableEvent) {
                    setSelectedRow(rowId);
                    if (row.onClick) row.onClick({ ...row });
                    else onTableEvent!({ ...row } as TableRow);
                  }
                }}
                className={`
                  ${applyfadeOnScrollClassName ? fadeOnScrollClassName : ''}
                  ${isEven ? 'bg-white dark:bg-slate-800' : 'bg-gray-50/50 dark:bg-slate-800/50'}
                  ${
                    isSelected
                      ? '!bg-orange/5 dark:!bg-teal-500/10 ring-1 ring-inset ring-orange/20 dark:ring-teal-500/20'
                      : ''
                  }
                  hover:bg-gradient-to-r hover:from-gray-100/80 hover:to-gray-50/50 dark:hover:from-slate-700/60 dark:hover:to-slate-700/30
                  transition-all duration-150 ease-out
                  cursor-pointer
                  group
                  ${row.className || ''}
                `.trim()}
              >
                {row.cells.map((cell, cellIndex) => {
                  const isVisible = cell.isVisible !== undefined ? cell.isVisible : true;
                  if (!isVisible) return null;
                  const isFirstCell = cellIndex === 0;
                  return cell.type === CellTypeEnum.TH ? (
                    <th
                      key={`cell-${cellIndex}`}
                      scope="row"
                      className={`${cell.cellContentTemplate ? '' : 'py-4 px-4 whitespace-nowrap font-medium'} ${
                        isFirstCell ? 'text-gray-900 dark:text-white' : ''
                      } ${stickyBodyCellClass(cellIndex, isEven)} ${cell.className || ''}`.trim()}
                    >
                      {cell.cellContentTemplate ? cell.cellContentTemplate(cell, t, row.rowData) : `${cell.text}`}
                    </th>
                  ) : (
                    <td
                      key={`cell-${cellIndex}`}
                      className={`${
                        cell.cellContentTemplate ? '' : 'py-4 px-4 whitespace-nowrap'
                      } ${stickyBodyCellClass(cellIndex, isEven)} ${cell.className || ''}`.trim()}
                    >
                      {cell.cellContentTemplate ? cell.cellContentTemplate(cell, t, row.rowData) : cell.text}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isLoading && (
        <div className="py-6 bg-gradient-to-b from-white to-gray-50/50 dark:from-slate-800 dark:to-slate-800/50">
          <TableSkeleton rows={3} columns={5} />
        </div>
      )}
    </div>
  );
};

export default ColumnSortTable;
